import { useEffect, useMemo } from 'react';
import { Link } from 'react-router';

import {
  getOnlineCourseEventProperties,
  onlineCoursePracticeModeQuestionSwapped,
  onlineCoursPracticeQuestionTokenPurchased,
} from 'my-core/gtm-events';

import PracticeQuestion from 'my-components/OnlineCoursePracticeQuestion/PracticeQuestion';

import PracticeModeSectionEmptyState from './PracticeModeSectionEmptyState';
import PracticeModeSectionNavigation from './PracticeModeSectionNavigation';

export default function PracticeModeSection({
  activeQuestion,
  activeSubmission,
  canCheckSolution,
  canFetchQuestion,
  canTokenPurchase,
  courseSubscription,
  fetchingSubmissions,
  fetchNewQuestionStatus,
  isNewQuestion,
  nextAndPrev,
  onFetchNewQuestion,
  onlineCourse,
  onlineCourseUser,
  onPurchase,
  onResetSubmission,
  onSaveSubmission,
  // onStartTrial,
  onTokenPurchase,
  remainingQuestions,
  // savingSubmission,
  sectionId,
  submissions,
  subscription,
  // subscriptionTrialStatus,
  tokenPurchaseStatus,
  totalQuestions,
  userCourse,
}) {
  const buttonProps = useMemo(
    () =>
      isNewQuestion ?
        {
          component: Link,
          nativeButton: false,
          onClick: btnPosition => {
            onFetchNewQuestion();
            onlineCoursePracticeModeQuestionSwapped(onlineCourse, activeQuestion, btnPosition);
          },
          to: { search: '' },
          replace: true,
          disabled: !canFetchQuestion,
          children: 'Swap Question',
        }
      : remainingQuestions ?
        {
          component: Link,
          nativeButton: false,
          to: { search: '' },
          children: 'Next Question',
        }
      : nextAndPrev.next && {
          component: Link,
          nativeButton: false,
          to: nextAndPrev.next.path,
          children: 'Next Section',
        },
    [
      activeQuestion,
      isNewQuestion,
      onFetchNewQuestion,
      canFetchQuestion,
      nextAndPrev.next,
      onlineCourse,
      remainingQuestions,
    ],
  );

  const eventTrackingProperties = useMemo(
    () => ({
      category: 'Online Course Practice Mode',
      ...getOnlineCourseEventProperties(onlineCourse),
      is_practice_mode: true,
      instructor_id: activeQuestion?.user?.id,
      instructor_name: activeQuestion?.user?.name,
      activity_type: 'QuizQuestion',
      activity_id: activeQuestion?.id,
      activity_subtype: activeQuestion?.type,
      video_length: activeQuestion?.video?.duration,
    }),
    [activeQuestion, onlineCourse],
  );

  useEffect(() => {
    if (tokenPurchaseStatus?.success) {
      onlineCoursPracticeQuestionTokenPurchased(eventTrackingProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPurchaseStatus]);

  // const startTrial = useCallback(() => onStartTrial(eventTrackingProperties), [eventTrackingProperties, onStartTrial]);

  const fetching = fetchingSubmissions || !!fetchNewQuestionStatus?.fetching;

  if (!totalQuestions) return <PracticeModeSectionEmptyState nextAndPrev={nextAndPrev} />;
  return (
    <>
      <PracticeModeSectionNavigation
        activeQuestionId={activeQuestion?.id}
        isNewQuestion={isNewQuestion}
        primaryButtonProps={buttonProps}
        remainingQuestions={remainingQuestions}
        submissions={submissions}
      />
      {(activeQuestion || fetching) && (
        <PracticeQuestion
          key={activeQuestion?.id || 'loading'}
          canCheckSolution={canCheckSolution}
          canTokenPurchase={canTokenPurchase}
          courseSubscription={courseSubscription}
          eventTrackingProperties={eventTrackingProperties}
          fetching={fetching}
          onlineCourse={onlineCourse}
          onlineCourseUser={onlineCourseUser}
          onPurchase={onPurchase}
          onResetSubmission={onResetSubmission}
          onSaveSubmission={onSaveSubmission}
          // onStartTrial={startTrial}
          onTokenPurchase={onTokenPurchase}
          practiceModeSectionId={sectionId}
          quizQuestion={activeQuestion}
          secondaryCTAProps={buttonProps}
          submission={activeSubmission}
          subscription={subscription}
          // subscriptionTrialStatus={subscriptionTrialStatus}
          tokenPurchaseStatus={tokenPurchaseStatus}
          userCourse={userCourse}
        />
      )}
    </>
  );
}
